import { ApplicationRef, Component, EventEmitter, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ProductCategory } from '../../../../../../../shared/products/models/ProductCategory';
import { ProductDescription } from '../../../../../../../shared/products/models/ProductDescription';
import { DangerousGoodsCode } from '../../../../../../../shared/products/models/DangerousGoodsCode';
import { NgSelectModule } from '@ng-select/ng-select';
import { JsonPipe } from '@angular/common';
import { ProductsService } from '../../../../../../../shared/products/products.service';
import { AddTagFn } from '@ng-select/ng-select/lib/ng-select.component';
import { Subscription, firstValueFrom } from 'rxjs';
import { ToasterService } from '../../../../../../../shared/toaster/toaster.service';
import { wholeNumberValidator } from '../../../../../../../shared/validators/whole-number.validator';
import { atLeastOneRequiredValidator } from '../../../../../../../shared/validators/at-least-one-required.validator';
import { productCategorySelectedValidator } from '../../../../../../../shared/validators/product-category-selected.validator';
import { productDescriptionSelectedValidator } from '../../../../../../../shared/validators/product-description-selected.validator';
import { productDescriptionLengthValidator } from '../../../../../../../shared/validators/product-description-length-validator';

@Component({
  selector: 'app-product-detail-form',
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule, JsonPipe],
  templateUrl: './product-detail-form.component.html',
  styleUrl: './product-detail-form.component.scss'
})
export class ProductDetailFormComponent implements OnInit, OnDestroy {

  @Input()
  disableEmitter!: EventEmitter<boolean>;
  @Input()
  formGroup!: FormGroup;

  productCategories: ProductCategory[] | null = null;

  @Input()
  dangerousProductCodes: DangerousGoodsCode[] | null = null;

  // this is used to memo the product descriptions for each product category
  productDescriptions!: any;
  focusedProductDescriptionIndex: number | null = null;
  productLoading: boolean = false;

  lockdown: boolean = false;

  private subscriptions: Subscription[] = [];

  constructor(private readonly formBuilder: FormBuilder,
    private readonly productService: ProductsService,
    private readonly toastService: ToasterService,
    private readonly appRef: ApplicationRef
  ) { }
  ngOnDestroy(): void {
    if (this.subscriptions.length > 0) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
  }

  ngOnInit(): void {
    this.disableEmitter.subscribe((value) => {
      if (value) {
        this.formGroup.disable({ emitEvent: false });
        this.getProductList().controls.forEach((control) => {
          control.get('category')?.disable({ emitEvent: false });
          control.get('description')?.disable({ emitEvent: false });
          control.get('quantity')?.disable({ emitEvent: false });
          control.get('weightKg')?.disable({ emitEvent: false });
          control.get('volumeM3')?.disable({ emitEvent: false });
          control.get('dangerousGoodsCode')?.disable({ emitEvent: false });
        });
        this.lockdown = true;
      } else {
        this.formGroup.enable({ emitEvent: false });
        this.getProductList().controls.forEach((control) => {
          control.get('category')?.enable({ emitEvent: false });
          control.get('description')?.enable({ emitEvent: false });
          control.get('quantity')?.enable({ emitEvent: false });
          control.get('weightKg')?.enable({ emitEvent: false });
          control.get('volumeM3')?.enable({ emitEvent: false });
          control.get('dangerousGoodsCode')?.enable({ emitEvent: false });
        });
        this.lockdown = false;
      }
    });
    this.formGroup.addControl('productDetail', this.formBuilder.group({
      products: this.formBuilder.array([])
    }));
    this.addProduct();
    this.productDescriptions = {};
    const freightPayerControl = this.formGroup.get("freightPayer");

    if (freightPayerControl) {
      if (!freightPayerControl.value) {
        this.lockdown = true;
      }
      const freightPayerSubscription = freightPayerControl.valueChanges.subscribe((freightPayerId: number | undefined | null) => {
        if (freightPayerId) {
          this.lockdown = false;
          this.productService.getProductCategories(freightPayerId).subscribe((data: ProductCategory[]) => {
            this.productCategories = data
          });
        } else {
          this.lockdown = true;
        }
      })

      this.subscriptions.push(freightPayerSubscription);
    }

    this.getProductList().valueChanges.subscribe(async (data) => {
      this.productLoading = true;
      const assignProductDescriptions = (descriptions: ProductDescription[], dataIndex: number) => {
        if (!data[dataIndex].description?.productId) {
          return;
        }
        const productDescription = this.productDescriptions[data[dataIndex].category.productCategoryId].find((productDescription: ProductDescription) => productDescription.productId === data[dataIndex].description.productId);
        if (productDescription) {
          this.getProductList().at(dataIndex).get('description')?.patchValue({
            name: productDescription.name,
            productId: productDescription.productId
          }, { emitEvent: false });
        }
      }
      await Promise.all(
        data.map(async (product: any, index: number) => {
          if (product.category && product.category.productCategoryId) {
            if (this.productDescriptions[product.category.productCategoryId]) {
              assignProductDescriptions(this.productDescriptions[product.category.productCategoryId], index);
            } else {
              const descriptionSearch$ = this.productService.getProductDescriptionsForCategory(product.category.productCategoryId);
              const descriptionResult = await firstValueFrom(descriptionSearch$);

              this.productDescriptions[product.category.productCategoryId] = descriptionResult;
              assignProductDescriptions(this.productDescriptions[product.category.productCategoryId], index);
            }
          }
        })
      );
      this.productLoading = false;
    });
  }

  compareWith(a: any, b: any) {
    return a.productId === b.productId;
  }

  compareWithCategory(a: any, b: any) {
    return a.productCategoryId === b.productCategoryId;
  }

  getProductDescriptionOptions(index: number) {
    const control = this.getProductList().at(index)
    if (!control.value.category) {
      return [];
    }
    const value = control.value.category.productCategoryId;
    if (value === null || value === 0) {
      return [];
    }
    return this.productDescriptions[value] || [];
  }

  createNewProductDescriptionTagFunction: AddTagFn = (term: string) => {
    return this.createNewProductDescription(term);
  };

  focusedProductDescriptionInput(index: number | null) {
    this.focusedProductDescriptionIndex = index;
  }

  onProductCategoryChange(event: ProductCategory | undefined, index: number) {
    if (!event) {
      return;
    }

    this.getProductList().at(index).patchValue({
      category: {
        name: event.name,
        productCategoryId: event.productCategoryId
      },
      description: {
        name: '',
        productId: null
      }
    }, { emitEvent: false });

    if (this.productDescriptions[event.productCategoryId]) {
      return;
    } else {
      this.productService.getProductDescriptionsForCategory(event.productCategoryId).subscribe((data: ProductDescription[]) => {
        this.productDescriptions[event.productCategoryId] = data;
      });
    }
  }

  onProductDescriptionChange(event: ProductDescription | undefined, index: number) {
    if (!event) {
      return;
    }

    this.getProductList().at(index).patchValue({
      description: {
        productId: event.productId,
        name: event.name
      }
    }, { emitEvent: false });
  }

  createNewProductDescription(term: string) {
    return new Promise((resolve) => {
      if (this.focusedProductDescriptionIndex !== null) {
        const index = this.focusedProductDescriptionIndex;
        const category = this.getCategoryControl(index).value;

        if (!category.productCategoryId) {
          return;
        }

        if (term.length >= 100) {
          this.toastService.showDanger("Product description cannot exceed 100 characters.");
          return;
        }
        this.productService.createProductDescription(category.productCategoryId, term).subscribe((result) => {
          this.productDescriptions[category.productCategoryId].push(result);
          this.getProductList().at(index).patchValue({
            description: result.productId,
          });

          resolve(result);
        })
      }
    });
  }

  getProductList() {
    return this.formGroup.get('productDetail.products') as FormArray;
  }

  getProductDetailForm() {
    return this.formGroup.get('productDetail') as FormGroup;
  }

  removeProduct(index: number) {
    const newList = this.getProductList().value.map((product: any, i: number) => {
      if (i !== index) {
        return product;
      }
    }).filter((product: any) => product !== undefined);

    this.appRef.tick();
    this.getProductList().clear();

    newList.forEach((product: any) => {
      this.getProductList().push(this.formBuilder.group({
        category: this.formBuilder.control({
          name: product.category?.name,
          productCategoryId: product.category?.productCategoryId
        }, [productCategorySelectedValidator()]),
        description: this.formBuilder.control({
          name: product.description?.name,
          productId: product.description?.productId
        }, [productDescriptionSelectedValidator(), productDescriptionLengthValidator()]),
        quantity: this.formBuilder.control(product.quantity, [atLeastOneRequiredValidator(['quantity', 'weightKg', 'volumeM3']), wholeNumberValidator(), Validators.min(1)]),
        weightKg: this.formBuilder.control(product.weightKg, [atLeastOneRequiredValidator(['quantity', 'weightKg', 'volumeM3'])]),
        volumeM3: this.formBuilder.control(product.volumeM3, [atLeastOneRequiredValidator(['quantity', 'weightKg', 'volumeM3'])]),
        dangerousGoodsCode: this.formBuilder.control(product.dangerousGoodsCode)
      }));
      this.appRef.tick();
    });
    this.appRef.tick();
  }

  getCategoryControl(index: number) {
    return this.getProductList().at(index).get('category') as FormControl;
  }

  addProduct() {
    this.getProductList().push(this.formBuilder.group({
      category: this.formBuilder.control({
        name: '',
        productCategoryId: null
      }, [productCategorySelectedValidator()]),
      description: this.formBuilder.control({
        name: '',
        productId: null
      }, [productDescriptionSelectedValidator(), productDescriptionLengthValidator()]),
      quantity: this.formBuilder.control(1, [atLeastOneRequiredValidator(['quantity', 'weightKg', 'volumeM3']), wholeNumberValidator(), Validators.min(1)]),
      weightKg: this.formBuilder.control(0, [atLeastOneRequiredValidator(['quantity', 'weightKg', 'volumeM3'])]),
      volumeM3: this.formBuilder.control(0, [atLeastOneRequiredValidator(['quantity', 'weightKg', 'volumeM3'])]),
      dangerousGoodsCode: this.formBuilder.control(null)
    }));
  }
}
